import React, { useState } from 'react';
import fetchMethodRequest from '../../../config/service';
import showToasterMessage from '../../UI/ToasterMessage/toasterMessage';
import { ClipLoader } from 'react-spinners';

const FilePredictNow = ({ fileDetails }) => {
  // Local prediction state — starts from stored data, updated after re-predict
  const [prediction, setPrediction] = useState(
    fileDetails?.details?.casePredictionAnalysis || null
  );
  const [isRepredicting, setIsRepredicting] = useState(false);

  const fileId = fileDetails?.fileId;

  const handleRePredict = async () => {
    // Do not predict for completed cases
    const caseStatus = fileDetails?.details?.caseStatus?.toLowerCase();
    if (caseStatus === 'completed') {
      showToasterMessage('Case is completed; no predictions needed.', 'info');
      return;
    }
    if (!fileId || isRepredicting) return;
    setIsRepredicting(true);
    try {
      const response = await fetchMethodRequest(
        'POST',
        `files/re-predict/${fileId}`,
        {}
      );
      if (response && response.success && response.casePredictionAnalysis) {
        setPrediction(response.casePredictionAnalysis);
        showToasterMessage('Prediction updated successfully!', 'success');
      } else {
        showToasterMessage(response?.message || 'Re-prediction failed. Please try again.', 'error');
      }
    } catch (error) {
      showToasterMessage('An error occurred during re-prediction', 'error');
    } finally {
      setIsRepredicting(false);
    }
  };

  const impactColor = (impact) => {
    if (!impact) return '#6b7280';
    const l = impact.toLowerCase();
    if (l === 'high') return '#ef4444';
    if (l === 'medium') return '#f59e0b';
    return '#22c55e';
  };

  if (!prediction) {
    return (
      <div style={styles.container}>
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>📊</div>
          <p style={styles.emptyTitle}>No Prediction Available</p>
          <p style={styles.emptySubtitle}>
            This file has not been analyzed yet. Click Re-Predict to generate a new analysis.
          </p>
        </div>
        <button
          style={{ ...styles.rePredictBtn, marginTop: '12px', opacity: isRepredicting ? 0.7 : 1, cursor: isRepredicting ? 'not-allowed' : 'pointer' }}
          onClick={handleRePredict}
          disabled={isRepredicting}
        >
          {isRepredicting ? (
            <><ClipLoader color="#fff" size={14} /> Analyzing...</>
          ) : (
            <><span>🔄</span> Generate Prediction</>
          )}
        </button>
      </div>
    );
  }

  const winPct = parseInt((prediction.winProbability || '0').replace('%', ''), 10) || 0;

  return (
    <div style={styles.container}>

      {/* Win Probability */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.cardIcon}>🎯</span>
          <span style={styles.cardTitle}>Win Probability</span>
        </div>
        <div style={styles.winRow}>
          <span style={styles.winPercent}>{prediction.winProbability}</span>
        </div>
        <div style={styles.progressBg}>
          <div style={{ ...styles.progressFill, width: `${winPct}%` }} />
        </div>
      </div>

      {/* Summary */}
      {prediction.summary && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardIcon}>📋</span>
            <span style={styles.cardTitle}>Summary</span>
          </div>
          <p style={styles.bodyText}>{prediction.summary}</p>
        </div>
      )}

      {/* Detailed Prediction */}
      {prediction.detailedPrediction && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardIcon}>🔍</span>
            <span style={styles.cardTitle}>Detailed Prediction</span>
          </div>
          <p style={styles.bodyText}>{prediction.detailedPrediction}</p>
        </div>
      )}

      {/* Step-by-Step Plan */}
      {prediction.stepByStepPlan && prediction.stepByStepPlan.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardIcon}>📝</span>
            <span style={styles.cardTitle}>Action Plan</span>
          </div>
          <ol style={styles.orderedList}>
            {prediction.stepByStepPlan.map((step, i) => (
              <li key={i} style={styles.listItem}>
                <span style={styles.stepNum}>{i + 1}</span>
                <span style={styles.stepText}>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Risk Assessment */}
      {prediction.riskAssessment && prediction.riskAssessment.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardIcon}>⚠️</span>
            <span style={styles.cardTitle}>Risk Assessment</span>
          </div>
          <div style={styles.riskGrid}>
            {prediction.riskAssessment.map((r, i) => (
              <div key={i} style={styles.riskItem}>
                <div style={styles.riskTop}>
                  <span style={styles.riskName}>{r.risk}</span>
                  <span style={{ ...styles.impactBadge, background: impactColor(r.impact) }}>
                    {r.impact}
                  </span>
                </div>
                <p style={styles.riskMitigation}>{r.mitigation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Strategic Paths */}
      {prediction.strategicPaths && prediction.strategicPaths.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardIcon}>🧭</span>
            <span style={styles.cardTitle}>Strategic Paths</span>
          </div>
          {prediction.strategicPaths.map((sp, i) => (
            <div key={i} style={styles.strategyItem}>
              <p style={styles.strategyName}>{sp.strategy}</p>
              <p style={styles.strategyDesc}>{sp.description}</p>
              <div style={styles.prosConsRow}>
                <div style={styles.prosBox}>
                  <span style={styles.prosLabel}>✅ Pros</span>
                  <span style={styles.prosConsText}>{sp.pros}</span>
                </div>
                <div style={styles.consBox}>
                  <span style={styles.consLabel}>⚠️ Cons</span>
                  <span style={styles.prosConsText}>{sp.cons}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Legal Precedents */}
      {prediction.legalPrecedents && prediction.legalPrecedents.filter(p => p.citation !== 'N/A').length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardIcon}>⚖️</span>
            <span style={styles.cardTitle}>Legal Precedents</span>
          </div>
          {prediction.legalPrecedents.filter(p => p.citation !== 'N/A').map((lp, i) => (
            <div key={i} style={styles.precedentItem}>
              <p style={styles.citation}>{lp.citation}</p>
              <p style={styles.relevance}>{lp.relevance}</p>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      {prediction.generatedAt && (
        <p style={styles.footer}>
          Generated: {new Date(prediction.generatedAt).toLocaleString()}
        </p>
      )}

      {/* ── Re-Predict Button ── */}
      <button
        id="re-predict-btn"
        style={{
          ...styles.rePredictBtn,
          opacity: isRepredicting ? 0.7 : 1,
          cursor: isRepredicting ? 'not-allowed' : 'pointer',
        }}
        onClick={handleRePredict}
        disabled={isRepredicting}
        title="Regenerate prediction using the latest historical case data"
      >
        {isRepredicting ? (
          <>
            <ClipLoader color="#fff" size={14} />
            Analyzing with Historical Data...
          </>
        ) : (
          <>
            <span>🔄</span>
            Re-Predict with Latest Data
          </>
        )}
      </button>

    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '6px 4px',
    overflowY: 'auto',
    maxHeight: "calc(100vh - 300px)",
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
    color: '#94a3b8',
  },
  emptyIcon: { fontSize: '36px', marginBottom: '10px' },
  emptyTitle: { fontSize: '14px', fontWeight: '600', color: '#475569', margin: '0 0 6px' },
  emptySubtitle: { fontSize: '11px', color: '#94a3b8', lineHeight: '1.5', margin: 0 },

  card: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '10px 12px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '8px',
    paddingBottom: '6px',
    borderBottom: '1px solid #e2e8f0',
  },
  cardIcon: { fontSize: '13px' },
  cardTitle: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#1e293b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  bodyText: {
    fontSize: '11.5px',
    color: '#475569',
    lineHeight: '1.6',
    margin: 0,
  },

  winRow: { display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '6px' },
  winPercent: { fontSize: '28px', fontWeight: '800', color: '#6366f1' },
  progressBg: {
    height: '6px',
    background: '#e2e8f0',
    borderRadius: '99px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
    borderRadius: '99px',
    transition: 'width 0.6s ease',
  },

  orderedList: { margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' },
  listItem: { display: 'flex', alignItems: 'flex-start', gap: '8px' },
  stepNum: {
    minWidth: '20px',
    height: '20px',
    background: '#6366f1',
    color: '#fff',
    borderRadius: '50%',
    fontSize: '10px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '1px',
  },
  stepText: { fontSize: '11.5px', color: '#475569', lineHeight: '1.5' },

  riskGrid: { display: 'flex', flexDirection: 'column', gap: '8px' },
  riskItem: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '8px 10px',
  },
  riskTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  riskName: { fontSize: '11.5px', fontWeight: '600', color: '#1e293b' },
  impactBadge: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#fff',
    padding: '2px 8px',
    borderRadius: '99px',
  },
  riskMitigation: { fontSize: '11px', color: '#64748b', margin: 0, lineHeight: '1.5' },

  strategyItem: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '8px 10px',
    marginBottom: '6px',
  },
  strategyName: { fontSize: '11.5px', fontWeight: '700', color: '#1e293b', margin: '0 0 3px' },
  strategyDesc: { fontSize: '11px', color: '#64748b', margin: '0 0 6px', lineHeight: '1.5' },
  prosConsRow: { display: 'flex', gap: '6px' },
  prosBox: {
    flex: 1,
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '4px',
    padding: '4px 6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  consBox: {
    flex: 1,
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '4px',
    padding: '4px 6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  prosLabel: { fontSize: '10px', fontWeight: '700', color: '#16a34a' },
  consLabel: { fontSize: '10px', fontWeight: '700', color: '#d97706' },
  prosConsText: { fontSize: '10.5px', color: '#374151', lineHeight: '1.4' },

  precedentItem: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderLeft: '3px solid #6366f1',
    borderRadius: '4px',
    padding: '8px 10px',
    marginBottom: '6px',
  },
  citation: { fontSize: '11.5px', fontWeight: '600', color: '#6366f1', margin: '0 0 3px' },
  relevance: { fontSize: '11px', color: '#64748b', margin: 0, lineHeight: '1.5' },

  footer: {
    fontSize: '10px',
    color: '#94a3b8',
    textAlign: 'right',
    margin: '2px 0 0',
  },

  // Re-Predict button
  rePredictBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.3px',
    cursor: 'pointer',
    marginTop: '4px',
    marginBottom: '4px',
    boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
    transition: 'opacity 0.2s ease',
  },
};

export default FilePredictNow;
